import { useState, useCallback, useEffect } from 'react';
import { putCarrierProfile } from 'utils/network/http';
import useDocsCarrierProfile from 'hooks/useDocsCarrierProfile/useDocsCarrierProfile';
import { useHistory } from 'react-router-dom';
import { ROUTES } from 'utils/constants/routes';
import merge from 'lodash.merge';

const useDocsCarrierForm = () => {
  const [loadingForm, setLoadingForm] = useState(false);
  const [fieldsWithError, setFieldsWithError] = useState({});
  const {
    loadingProfile,
    carrierProfile,
    silentFetchCarrierDocsProfile,
    carrierUuid,
  } = useDocsCarrierProfile();
  const [carrierData, setCarrierData] = useState({});
  const history = useHistory();

  const validateFields = useCallback(() => {
    const requiredField = (value) => value && value !== '';
    const field_validations = {
      carrier_type: requiredField,
      name: requiredField,
      tax_number: requiredField,
      zipcode: requiredField,
      city: requiredField,
      address_line: requiredField,
    };
    const flattenData = { ...carrierData, ...carrierData.billing_address };
    const fieldsErrorsArray = Object.keys(flattenData).filter(
      (field) =>
        field_validations[field] &&
        !field_validations[field](flattenData[field])
    );
    const fieldErrorsObject = fieldsErrorsArray.reduce(
      (acc, current) => ({ ...acc, [current]: true }),
      {}
    );
    setFieldsWithError(fieldErrorsObject);
  }, [carrierData]);

  const handleFieldChange = useCallback(
    (field, value) => {
      setCarrierData({
        ...carrierData,
        [field]: value,
      });
    },
    [carrierData]
  );

  const handleServerErrors = useCallback((serverErrors) => {
    setFieldsWithError({ ...serverErrors });
  }, []);

  const handleSubmit = useCallback(async () => {
    validateFields();
    try {
      setLoadingForm(true);
      const mergedData = merge(carrierProfile.carrier_info, carrierData);
      await putCarrierProfile(carrierUuid, mergedData);
      await silentFetchCarrierDocsProfile();
      history.push(ROUTES.DOCS_DOCUMENTS.DEFAULT(carrierUuid));
    } catch (error) {
      console.error('Error putCarrierProfile', error);
      handleServerErrors(error.response?.data);
    } finally {
      setLoadingForm(false);
    }
  }, [
    carrierData,
    carrierUuid,
    carrierProfile.carrier_info,
    validateFields,
    handleServerErrors,
    history,
    silentFetchCarrierDocsProfile,
  ]);

  useEffect(() => {
    validateFields();
  }, [carrierData, validateFields]);

  return {
    handleFieldChange,
    carrierData,
    fieldsWithError,
    handleSubmit,
    serverCarrierData: carrierProfile.carrier_info || {},
    loadingProfile,
    loadingForm,
    countrySpecificFields:
      carrierProfile.carrier_info?.country_specific_extra_fields,
  };
};

export default useDocsCarrierForm;
